import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Box,
  DialogContentText,
  Grow,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { produce } from 'immer';
import _ from 'lodash';
import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import FileDropZone from '@sophocles/components/FileDropZone/FileDropZone';
import ResponsiveDialog from '@sophocles/components/ResponsiveDialog';
import { formatBytes } from '@sophocles/utils/formatUtils';

const ACCEPTED_IMAGE_TYPES = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
  'image/gif': ['.gif'],
  'image/heic': ['.heic'],
  'image/heif': ['.heif'],
};

const buildUploadImageValidationSchema = t =>
  yup.object({
    images: yup.array().of(
      yup.object({
        file: yup.string().required(t('media.fileUploadRequired', 'File upload is required')),
        comment: yup.string(),
      }),
    ),
  });

function UploadImageDialog({ open, onSubmit, onClose }) {
  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const [data, setData] = useState(null);

  const formik = useFormik({
    initialValues: {
      images: [],
    },
    validationSchema: buildUploadImageValidationSchema(t),
    onSubmit: values => {
      const submissionData = values.images.map(item => ({
        file: files[item.file][0],
        comment: item.comment,
      }));

      onSubmit(submissionData);

      setFiles([]);
      formik.resetForm();
    },
  });

  const handleClose = () => {
    formik.resetForm();
    setFiles([]);
    setData(null);
    onClose();
  };

  const removeFile = index => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
  };

  return (
    <ResponsiveDialog
      title={t('media.uploadImages', 'Upload Images')}
      open={open}
      onSubmit={formik.submitForm}
      submitText={t('general.submit', 'Submit')}
      onClose={handleClose}
      canSubmit={data != null || (!formik.isSubmitting && Object.keys(formik.errors).length === 0 && formik.dirty)}
      sx={{ '& .MuiDialogContent-root > :not(style)': { mb: 2 } }}
    >
      {!data ? (
        <Fragment>
          <DialogContentText>
            {t(
              'media.uploadInstructions',
              'To upload images, please select a file and enter all required details here.',
            )}
          </DialogContentText>
          <FileDropZone
            config={{ accept: ACCEPTED_IMAGE_TYPES }}
            onFilesAdded={uploadedFiles => {
              const newData = [];
              const newFormikValues = [];
              uploadedFiles.forEach((fileData, index) => {
                const f = fileData[0];
                const d = fileData[1];
                const fileInfo = [f, d];
                newData.push(fileInfo);
                newFormikValues.push({ file: index, comment: '' });
              });

              setFiles(newData);
              formik.setFieldValue('images', newFormikValues);
            }}
            onFileRemoved={i => {
              setFiles(
                produce(files, draft => {
                  draft.splice(i, 1);
                  return draft;
                }),
              );
              formik.setFieldValue(
                'images',
                produce(formik.values.images, draft => {
                  draft.splice(i, 1);
                  return draft;
                }),
              );
            }}
          >
            {({ isBusy }) => {
              return (
                <Grow in={!isBusy}>
                  <Typography variant="body1">
                    {t('media.dragDropPrompt', 'Drag & drop or click to select files')}
                  </Typography>
                </Grow>
              );
            }}
          </FileDropZone>
          <List>
            {formik.values.images.map((x, i) => {
              if (files.length === 0) {
                return null;
              }
              const imageFileName = files[x.file][0].name;
              const imageBytes = formatBytes(files[x.file][0]).size;
              return (
                <ListItem key={imageFileName} sx={{ flexDirection: 'column' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      flexDirection: 'row',
                      width: '100%',
                      alignItems: 'center',
                    }}
                  >
                    <ListItemIcon>
                      <UploadFileIcon />
                    </ListItemIcon>
                    <ListItemText primary={imageFileName} secondary={imageBytes} />
                    <IconButton size="small" onClick={() => removeFile(i)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </ListItem>
              );
            })}
          </List>
        </Fragment>
      ) : (
        <DialogContentText>
          {t('media.finalizeUploadPrompt', 'To finalize upload, please hit Submit.')}
        </DialogContentText>
      )}
    </ResponsiveDialog>
  );
}

export default UploadImageDialog;
